import { useState } from 'react';
import { BoutonPrincipal, BoutonSecondaire, MessageErreur } from '../ui/Primitives';
import { ContenuProtege } from '../ui/ContenuProtege';
import type { Passation, Reponses } from '../../lib/useTestsPositionnement';

/**
 * Passation d'un test de positionnement, la même sur la tablette du centre et
 * dans l'espace de l'apprenant. Les questions arrivent sans corrigé ; une
 * question laissée vide vaut zéro, d'où la confirmation avant la remise, qui
 * est définitive. Les énoncés ne se copient, ne s'impriment ni ne se
 * téléchargent (`ContenuProtege`, TF-485) ; les réponses libres se reprennent.
 */
export function PassationTest({
  passation,
  enCours,
  erreur,
  onRemettre,
  onAnnuler,
}: {
  passation: Passation;
  enCours: boolean;
  erreur: string | null;
  onRemettre: (reponses: Reponses) => void;
  onAnnuler?: () => void;
}): JSX.Element {
  const [reponses, setReponses] = useState<Reponses>({});
  const [confirmation, setConfirmation] = useState(false);

  const sansReponse = passation.questions.filter((question) => {
    const reponse = reponses[question.id];
    return question.type === 'REPONSE_LIBRE'
      ? !(reponse?.texte ?? '').trim()
      : (reponse?.choix ?? []).length === 0;
  }).length;

  const cocher = (questionId: string, choixId: string, unique: boolean): void =>
    setReponses((courantes) => {
      const deja = courantes[questionId]?.choix ?? [];
      const choix = unique
        ? [choixId]
        : deja.includes(choixId)
          ? deja.filter((id) => id !== choixId)
          : [...deja, choixId];
      return { ...courantes, [questionId]: { choix } };
    });

  return (
    <ContenuProtege libelle="Ce test">
      <div className="space-y-5">
        {passation.consigne && (
          <p className="rounded-xl bg-codialis-sand px-4 py-3 text-sm text-codialis-navy">
            {passation.consigne}
          </p>
        )}

        <ol className="space-y-4">
          {passation.questions.map((question, rang) => (
            <li key={question.id} className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
              <fieldset className="min-w-0 space-y-3">
                <legend className="w-full text-sm font-semibold text-codialis-navy">
                  <span className="mr-2 text-codialis-gray">{rang + 1}.</span>
                  {question.enonce}
                  <span className="ml-2 text-[11px] font-normal text-codialis-gray">
                    {question.points} pt{question.points > 1 ? 's' : ''}
                    {question.type === 'CHOIX_MULTIPLE' ? ' · plusieurs réponses possibles' : ''}
                  </span>
                </legend>

                {question.type === 'REPONSE_LIBRE' ? (
                  <textarea
                    value={reponses[question.id]?.texte ?? ''}
                    onChange={(evenement) =>
                      setReponses((courantes) => ({
                        ...courantes,
                        [question.id]: { texte: evenement.target.value },
                      }))
                    }
                    maxLength={5000}
                    rows={4}
                    disabled={enCours}
                    aria-label={`Réponse à la question ${rang + 1}`}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20"
                  />
                ) : (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {question.choix.map((choix) => {
                      const unique = question.type === 'CHOIX_UNIQUE';
                      const coche = (reponses[question.id]?.choix ?? []).includes(choix.id);
                      return (
                        <label
                          key={choix.id}
                          className={`flex min-h-[48px] min-w-0 cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 text-sm transition ${
                            coche
                              ? 'border-codialis-navy bg-codialis-sand text-codialis-navy'
                              : 'border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type={unique ? 'radio' : 'checkbox'}
                            name={question.id}
                            checked={coche}
                            disabled={enCours}
                            onChange={() => cocher(question.id, choix.id, unique)}
                            className="h-5 w-5 shrink-0"
                          />
                          <span className="min-w-0 break-words">{choix.libelle}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </fieldset>
            </li>
          ))}
        </ol>

        {erreur && <MessageErreur message={erreur} />}

        {confirmation ? (
          <div className="space-y-3 rounded-2xl bg-amber-50 p-4">
            <p className="text-sm text-amber-900">
              {sansReponse > 0
                ? `${sansReponse} question(s) sans réponse vaudront zéro. `
                : ''}
              La remise est définitive : le test ne pourra plus être modifié.
            </p>
            <div className="flex flex-wrap gap-2">
              <BoutonPrincipal onClick={() => onRemettre(reponses)} disabled={enCours}>
                {enCours ? 'Remise…' : 'Remettre le test'}
              </BoutonPrincipal>
              <BoutonSecondaire onClick={() => setConfirmation(false)} disabled={enCours}>
                Revenir aux questions
              </BoutonSecondaire>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <BoutonPrincipal onClick={() => setConfirmation(true)} disabled={enCours}>
              Terminer le test
            </BoutonPrincipal>
            {onAnnuler && (
              <BoutonSecondaire onClick={onAnnuler} disabled={enCours}>
                Quitter sans remettre
              </BoutonSecondaire>
            )}
          </div>
        )}
      </div>
    </ContenuProtege>
  );
}
